/**
 * P1/P2 By Region - Bowler Chart
 * Shows weekly P1/P2 incident metrics (Count, MTTR, Lost Hours) for
 * Fulfilled By Maersk portfolio, broken down by geographic region.
 * Regions: IMEA, APA, NAM, LAM, EUR
 */

import React, { useState, useEffect, useMemo } from 'react';
import { authFetch } from '../services/authFetch';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Checkbox,
  ListItemText,
  TablePagination,
} from '@mui/material';
import { FileDownload, Refresh, Clear, HelpOutline, Close, MapOutlined, LockOutlined } from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import { INCIDENTS } from '../config/api';

// Access control – only these users may view the dashboard
const P1P2_BY_REGION_ALLOWED_EMAILS = new Set([
  'mahesh.s.bhosale@maersk.com',
]);

// ─── Types ───────────────────────────────────────────────────────────────────
interface RegionIncident {
  number: string;
  priority: string;
  sys_created_on_value: string;
  short_description: string;
  location_display_value: string;
  region: string;
  ttr_seconds: number | null;
}

interface PeriodMetrics {
  incidentCount: number;
  totalMTTRSeconds: number;
  lostHours: number;
  incidents: RegionIncident[];
}

interface RegionRow {
  region: string;
  metrics: { [period: string]: PeriodMetrics };
}

// ─── Constants ───────────────────────────────────────────────────────────────
const SELECT_ALL_OPTION = 'Select All';
const KPI_OPTIONS = ['Count', 'MTTR', 'Lost Hrs'];
const REGIONS = ['IMEA', 'APA', 'NAM', 'LAM', 'EUR'];

const REGION_LABELS: { [key: string]: string } = {
  IMEA: 'IMEA',
  APA: 'APA',
  NAM: 'NAM',
  LAM: 'LAM',
  EUR: 'EUR',
};

// Targets based on 99.5% availability (0.5% downtime) + count target of 1 per period
const TARGETS = {
  weekly:    { incidentCount: 1, mttrAvgMinutes: 30, lostHours: 0.84 },   // 7d × 24h × 0.5% = 0.84h (50m 24s)
  monthly:   { incidentCount: 1, mttrAvgMinutes: 30, lostHours: 3.6 },    // 30d × 24h × 0.5% = 3.6h  (3h 36m)
  quarterly: { incidentCount: 1, mttrAvgMinutes: 30, lostHours: 14.4 },   // 14h 24m as per 99.5% uptime chart
  yearly:    { incidentCount: 1, mttrAvgMinutes: 30, lostHours: 43.8 },   // 365d × 24h × 0.5% = 43.8h (1d 19h)
};

// ─── Helper Functions ────────────────────────────────────────────────────────
const formatLocalDate = (date: Date): string => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getISOWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const getISOYear = (date: Date): number => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
};

// Returns the Thursday of the ISO week — used to assign month/quarter by ISO week ownership
const getISOThursday = (date: Date): Date => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d;
};

const getPeriodKey = (date: Date, periodType: string): string => {
  if (periodType === 'weekly') {
    const week = getISOWeekNumber(date);
    const year = getISOYear(date);
    return `${year}-W${String(week).padStart(2, '0')}`;
  } else if (periodType === 'monthly') {
    // Use the ISO Thursday so e.g. Dec 29 (ISO W1 2026) → Jan 2026
    const thu = getISOThursday(date);
    return `${thu.getUTCFullYear()}-${String(thu.getUTCMonth() + 1).padStart(2, '0')}`;
  } else if (periodType === 'quarterly') {
    const thu = getISOThursday(date);
    const q = Math.floor(thu.getUTCMonth() / 3) + 1;
    return `${thu.getUTCFullYear()}-Q${q}`;
  }
  return `${getISOYear(date)}`;
};

const fmt = (d: Date) =>
  d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

const getPeriodDateRange = (period: string, periodType: string): string => {
  if (periodType === 'weekly') {
    const [yearStr, weekStr] = period.split('-W');
    const year = parseInt(yearStr);
    const week = parseInt(weekStr);
    // ISO week 1 Monday = Jan 4 of that year rolled back to Monday
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Day = jan4.getUTCDay() || 7;
    const w1Mon = new Date(jan4);
    w1Mon.setUTCDate(jan4.getUTCDate() - (jan4Day - 1));
    const start = new Date(w1Mon);
    start.setUTCDate(w1Mon.getUTCDate() + (week - 1) * 7);
    const end = new Date(start);
    end.setUTCDate(start.getUTCDate() + 6);
    return `${fmt(start)} – ${fmt(end)}`;
  } else if (periodType === 'monthly') {
    const [yearStr, monthStr] = period.split('-');
    const year = parseInt(yearStr);
    const month = parseInt(monthStr) - 1;
    // Find first day: walk backwards from 1st of month while ISO Thursday still maps to this month
    const startCursor = new Date(Date.UTC(year, month, 1));
    while (true) {
      const prev = new Date(startCursor);
      prev.setUTCDate(prev.getUTCDate() - 1);
      const thu = getISOThursday(prev);
      if (thu.getUTCFullYear() !== year || thu.getUTCMonth() !== month) break;
      startCursor.setUTCDate(startCursor.getUTCDate() - 1);
    }
    // Find last day: walk forwards from last day of month while ISO Thursday still maps to this month
    const endCursor = new Date(Date.UTC(year, month + 1, 0));
    while (true) {
      const next = new Date(endCursor);
      next.setUTCDate(next.getUTCDate() + 1);
      const thu = getISOThursday(next);
      if (thu.getUTCFullYear() !== year || thu.getUTCMonth() !== month) break;
      endCursor.setUTCDate(endCursor.getUTCDate() + 1);
    }
    return `${fmt(startCursor)} – ${fmt(endCursor)}`;
  } else if (periodType === 'quarterly') {
    const [yearStr, qStr] = period.split('-Q');
    const year = parseInt(yearStr);
    const startMonth = (parseInt(qStr) - 1) * 3;
    const endMonth = startMonth + 2;
    // Find first day of quarter using ISO Thursday
    const startCursor = new Date(Date.UTC(year, startMonth, 1));
    while (true) {
      const prev = new Date(startCursor);
      prev.setUTCDate(prev.getUTCDate() - 1);
      const thu = getISOThursday(prev);
      const prevQ = Math.floor(thu.getUTCMonth() / 3);
      const thisQ = Math.floor(startMonth / 3);
      if (thu.getUTCFullYear() !== year || prevQ !== thisQ) break;
      startCursor.setUTCDate(startCursor.getUTCDate() - 1);
    }
    // Find last day of quarter using ISO Thursday
    const endCursor = new Date(Date.UTC(year, endMonth + 1, 0));
    while (true) {
      const next = new Date(endCursor);
      next.setUTCDate(next.getUTCDate() + 1);
      const thu = getISOThursday(next);
      const nextQ = Math.floor(thu.getUTCMonth() / 3);
      const thisQ = Math.floor(startMonth / 3);
      if (thu.getUTCFullYear() !== year || nextQ !== thisQ) break;
      endCursor.setUTCDate(endCursor.getUTCDate() + 1);
    }
    return `${fmt(startCursor)} – ${fmt(endCursor)}`;
  }
  // yearly
  const year = parseInt(period);
  return `${fmt(new Date(Date.UTC(year, 0, 1)))} – ${fmt(new Date(Date.UTC(year, 11, 31)))}`;
};

const formatPeriodLabel = (period: string, periodType: string): string => {
  if (periodType === 'weekly') {
    const [year, week] = period.split('-W');
    return `W${week} - ${year}`;
  } else if (periodType === 'monthly') {
    const [year, month] = period.split('-');
    const d = new Date(parseInt(year), parseInt(month) - 1);
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  }
  return period;
};

const avgMTTRMinutes = (totalSeconds: number, count: number): number => {
  if (count === 0) return 0;
  return totalSeconds / count / 60;
};

// Display MTTR in hours (e.g. 0.82h)
const fmtMTTR = (minutes: number): string => `${(minutes / 60).toFixed(2)}h`;

const getCellColor = (actual: number, target: number): string => {
  if (!actual || actual === 0) return '#4caf50';
  return actual <= target ? '#4caf50' : '#f44336';
};

// ─── Component ───────────────────────────────────────────────────────────────
const P1P2ByRegion: React.FC = () => {
  const { accounts } = useMsal();
  const [rawData, setRawData] = useState<RegionIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [periodType, setPeriodType] = useState<'weekly' | 'monthly' | 'quarterly' | 'yearly'>('weekly');
  const [selectedTimeRange, setSelectedTimeRange] = useState('ytd');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [filterStartDate, setFilterStartDate] = useState<string | undefined>(undefined);
  const [filterEndDate, setFilterEndDate] = useState<string | undefined>(undefined);

  // Region filter — multi-select, default = all selected
  const [selectedRegions, setSelectedRegions] = useState<string[]>([...REGIONS]);

  // KPI filter — default to Count only
  const [selectedKPIs, setSelectedKPIs] = useState<string[]>(['Count']);

  // Detail dialog (used for region cells AND grand total)
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState<{ region: string; period: string; incidents: RegionIncident[] } | null>(null);
  const [dialogPage, setDialogPage] = useState(0);

  const userEmail = accounts[0]?.username;
  const isAccessAllowed = !!(userEmail && P1P2_BY_REGION_ALLOWED_EMAILS.has(userEmail.toLowerCase()));

  // ─── Fetch all data once ─────────────────────────────────────────────────────
  // No date params — load everything; all filtering is done on the frontend
  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(INCIDENTS.P1P2_BY_REGION);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRawData(json.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Load on mount
  useEffect(() => { fetchData(); }, []);

  // ─── Time range → update frontend filter dates only (no API call) ────────────
  useEffect(() => {
    const now = new Date();
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    let start = new Date(end);

    if (selectedTimeRange === 'custom') {
      if (customDateRange.start && customDateRange.end) {
        setFilterStartDate(customDateRange.start);
        setFilterEndDate(customDateRange.end);
      }
      return;
    }
    switch (selectedTimeRange) {
      case '7d':  start.setUTCDate(start.getUTCDate() - 7); break;
      case '30d': start.setUTCDate(start.getUTCDate() - 30); break;
      case '90d': start.setUTCDate(start.getUTCDate() - 90); break;
      case 'ytd': start = new Date(Date.UTC(end.getUTCFullYear(), 0, 1)); break;
    }

    setFilterStartDate(formatLocalDate(start));
    setFilterEndDate(formatLocalDate(end));
  }, [selectedTimeRange, customDateRange]);

  // ─── Filtered data ──────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    return rawData.filter(r => {
      if (filterStartDate && filterEndDate) {
        const date = r.sys_created_on_value.substring(0, 10);
        if (periodType === 'weekly') {
          // Weekly: simple date range filter
          if (date < filterStartDate || date > filterEndDate) return false;
        } else {
          // Non-weekly: compare ISO periods so e.g. Dec 29 (ISO W1 → Jan) is included
          const incPeriod = getPeriodKey(new Date(date + 'T00:00:00Z'), periodType);
          const startPeriod = getPeriodKey(new Date(filterStartDate + 'T00:00:00Z'), periodType);
          const endPeriod = getPeriodKey(new Date(filterEndDate + 'T00:00:00Z'), periodType);
          if (incPeriod < startPeriod || incPeriod > endPeriod) return false;
        }
      }
      if (selectedRegions.length > 0 && selectedRegions.length < REGIONS.length && !selectedRegions.includes(r.region)) return false;
      return true;
    });
  }, [rawData, filterStartDate, filterEndDate, selectedRegions, periodType]);

  // ─── Periods to display ─────────────────────────────────────────────────────
  // Generate ALL periods in the date range (not just those with data) so empty weeks/months still show
  const periodsToDisplay = useMemo(() => {
    const set = new Set<string>();

    if (filterStartDate && filterEndDate) {
      // Walk every day from start to end to collect all periods
      const cursor = new Date(filterStartDate + 'T00:00:00Z');
      const end = new Date(filterEndDate + 'T00:00:00Z');
      while (cursor <= end) {
        set.add(getPeriodKey(cursor, periodType));
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
    } else {
      // No date range (all data) — fall back to data-driven
      filteredData.forEach(r => {
        const d = new Date(r.sys_created_on_value.substring(0, 10) + 'T00:00:00Z');
        set.add(getPeriodKey(d, periodType));
      });
    }

    return Array.from(set).sort();
  }, [filteredData, filterStartDate, filterEndDate, periodType]);

  // ─── Region rows ────────────────────────────────────────────────────────────
  const regionRows = useMemo(() => {
    const map: { [region: string]: { [period: string]: PeriodMetrics } } = {};

    // Initialize all regions
    REGIONS.forEach(r => { map[r] = {}; });

    filteredData.forEach(inc => {
      const region = inc.region;
      if (!REGIONS.includes(region)) return; // skip Unknown

      const d = new Date(inc.sys_created_on_value.substring(0, 10) + 'T00:00:00Z');
      const period = getPeriodKey(d, periodType);

      if (!map[region][period]) {
        map[region][period] = { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0, incidents: [] };
      }
      const m = map[region][period];
      m.incidentCount++;
      m.incidents.push(inc);
      if (inc.ttr_seconds) {
        m.totalMTTRSeconds += inc.ttr_seconds;
        m.lostHours += inc.ttr_seconds / 3600;
      }
    });

    return REGIONS.map(region => ({ region, metrics: map[region] })) as RegionRow[];
  }, [filteredData, periodType]);

  // ─── KPI Autocomplete helper ────────────────────────────────────────────────
  const makeKpiAutocomplete = () => {
    const allOptions = [SELECT_ALL_OPTION, ...KPI_OPTIONS];
    const isAllSelected = selectedKPIs.length === KPI_OPTIONS.length;
    return (
      <Autocomplete
        multiple
        size="small"
        limitTags={1}
        options={allOptions}
        value={isAllSelected ? [SELECT_ALL_OPTION, ...selectedKPIs] : selectedKPIs}
        onChange={(_, newValue) => {
          const hasSelectAll = newValue.includes(SELECT_ALL_OPTION);
          if (hasSelectAll && !isAllSelected) {
            setSelectedKPIs([...KPI_OPTIONS]);
          } else if (!hasSelectAll && isAllSelected) {
            setSelectedKPIs([]);
          } else {
            setSelectedKPIs(newValue.filter(v => v !== SELECT_ALL_OPTION));
          }
        }}
        disableCloseOnSelect
        getOptionLabel={o => o}
        renderOption={(props, option, { selected: sel }) => (
          <li {...props}>
            <Checkbox checked={option === SELECT_ALL_OPTION ? isAllSelected : sel} sx={{ mr: 1 }} />
            <ListItemText primary={option} />
          </li>
        )}
        renderInput={params => <TextField {...params} label="KPI" />}
        sx={{ minWidth: 200 }}
      />
    );
  };

  // ─── Export CSV ─────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const target = TARGETS[periodType];
    const kpiCols = KPI_OPTIONS.filter(k => selectedKPIs.includes(k));
    const headers = [
      'Region',
      ...periodsToDisplay.flatMap(p =>
        kpiCols.map(k => `${formatPeriodLabel(p, periodType)} - ${k}`)
      ),
    ];
    const buildCols = (m: PeriodMetrics | null) => kpiCols.map(k => {
      if (!m) return '';
      if (k === 'Count') return m.incidentCount || '';
      if (k === 'MTTR') return m.incidentCount > 0 ? fmtMTTR(avgMTTRMinutes(m.totalMTTRSeconds, m.incidentCount)) : '';
      return m.lostHours > 0 ? m.lostHours.toFixed(2) : '';
    });
    const planValues = kpiCols.map(k => {
      if (k === 'Count') return target.incidentCount;
      if (k === 'MTTR') return fmtMTTR(target.mttrAvgMinutes);
      return target.lostHours;
    });
    const dataRows = regionRows.flatMap(row => [
      // PLAN row per region
      [`${row.region} - PLAN`, ...periodsToDisplay.flatMap(() => planValues)],
      // ACTUAL row per region
      [`${row.region} - ACTUAL`, ...periodsToDisplay.flatMap(period => buildCols(row.metrics[period] || null))],
    ]);

    const csv = [headers, ...dataRows]
      .map(r => r.map(c => `"${c}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `p1p2_by_region_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // ─── Cell render helper ─────────────────────────────────────────────────────
  const renderMetricCells = (m: PeriodMetrics | undefined, region: string, period: string) => {
    const target = TARGETS[periodType];
    const metrics = m || { incidentCount: 0, totalMTTRSeconds: 0, lostHours: 0, incidents: [] };
    const mttrAvg = avgMTTRMinutes(metrics.totalMTTRSeconds, metrics.incidentCount);

    const handleClick = () => {
      if (metrics.incidentCount > 0) {
        setDetailData({ region, period, incidents: metrics.incidents });
        setDialogPage(0);
        setDetailOpen(true);
      }
    };

    const lastKpi = [...KPI_OPTIONS].reverse().find(k => selectedKPIs.includes(k));

    return (
      <>
        {/* Count */}
        {selectedKPIs.includes('Count') && (
          <TableCell
            align="center"
            onClick={handleClick}
            sx={{
              fontSize: '0.8rem',
              minWidth: 52,
              fontWeight: metrics.incidentCount > 0 ? 700 : 400,
              backgroundColor: getCellColor(metrics.incidentCount, target.incidentCount),
              color: '#212121',
              cursor: metrics.incidentCount > 0 ? 'pointer' : 'default',
              borderRight: lastKpi === 'Count' ? '2px solid #37474f' : '1px solid #e0e0e0',
              '&:hover': metrics.incidentCount > 0 ? { opacity: 0.85 } : {},
            }}
          >
            {metrics.incidentCount || ''}
          </TableCell>
        )}

        {/* MTTR */}
        {selectedKPIs.includes('MTTR') && (
          <TableCell
            align="center"
            onClick={handleClick}
            sx={{
              fontSize: '0.8rem',
              minWidth: 68,
              fontWeight: mttrAvg > 0 ? 700 : 400,
              backgroundColor: getCellColor(mttrAvg, target.mttrAvgMinutes),
              color: '#212121',
              cursor: metrics.incidentCount > 0 ? 'pointer' : 'default',
              borderRight: lastKpi === 'MTTR' ? '2px solid #37474f' : '1px solid #e0e0e0',
              '&:hover': metrics.incidentCount > 0 ? { opacity: 0.85 } : {},
            }}
          >
            {mttrAvg > 0 ? fmtMTTR(mttrAvg) : ''}
          </TableCell>
        )}

        {/* Lost Hrs */}
        {selectedKPIs.includes('Lost Hrs') && (
          <TableCell
            align="center"
            onClick={handleClick}
            sx={{
              fontSize: '0.8rem',
              minWidth: 58,
              fontWeight: metrics.lostHours > 0 ? 700 : 400,
              backgroundColor: getCellColor(metrics.lostHours, target.lostHours),
              color: '#212121',
              cursor: metrics.incidentCount > 0 ? 'pointer' : 'default',
              borderRight: '2px solid #37474f',
              '&:hover': metrics.incidentCount > 0 ? { opacity: 0.85 } : {},
            }}
          >
            {metrics.lostHours > 0 ? metrics.lostHours.toFixed(2) : ''}
          </TableCell>
        )}
      </>
    );
  };

  const target = TARGETS[periodType];
  const lastKpi = [...KPI_OPTIONS].reverse().find(k => selectedKPIs.includes(k));

  // ─── Render ──────────────────────────────────────────────────────────────────
  if (!isAccessAllowed) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 2 }}>
        <LockOutlined sx={{ fontSize: 64, color: '#bdbdbd' }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#424242' }}>Access Restricted</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 420 }}>
          You don't have permission to view P1/P2 By Region.
        </Typography>
        {userEmail && (
          <Typography variant="caption" color="text.disabled">Logged in as: {userEmail}</Typography>
        )}
      </Box>
    );
  }
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MapOutlined sx={{ fontSize: 40, color: '#1565c0' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1565c0' }}>
              P1/P2 By Region
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fulfilled By Maersk · P1/P2 Incidents · Region view
            </Typography>
          </Box>
          <Tooltip title="P1/P2 incidents from Fulfilled By Maersk portfolio. Incidents are mapped to regions (IMEA, APA, NAM, LAM, EUR) via site location. Count, MTTR and Lost Hours are shown per week. Green = at/below target, Red = above target.">
            <IconButton size="small"><HelpOutline /></IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={() => fetchData()} disabled={loading}>
            Refresh
          </Button>
          <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={exportCSV} disabled={loading || rawData.length === 0}>
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Controls */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Region Filter — multi-select */}
          {(() => {
            const allSelected = selectedRegions.length === REGIONS.length;
            return (
              <Autocomplete
                multiple
                size="small"
                limitTags={2}
                options={[SELECT_ALL_OPTION, ...REGIONS]}
                value={allSelected ? [SELECT_ALL_OPTION, ...selectedRegions] : selectedRegions}
                onChange={(_, newValue) => {
                  const hasAll = newValue.includes(SELECT_ALL_OPTION);
                  if (hasAll && !allSelected) setSelectedRegions([...REGIONS]);
                  else if (!hasAll && allSelected) setSelectedRegions([]);
                  else setSelectedRegions(newValue.filter(v => v !== SELECT_ALL_OPTION));
                }}
                disableCloseOnSelect
                getOptionLabel={o => o}
                renderOption={(props, option, { selected: sel }) => (
                  <li {...props}>
                    <Checkbox checked={option === SELECT_ALL_OPTION ? allSelected : sel} sx={{ mr: 1 }} />
                    <ListItemText primary={option} />
                  </li>
                )}
                renderInput={params => <TextField {...params} label="Region" />}
                sx={{ minWidth: 220 }}
              />
            );
          })()}

          {/* Period Type */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Period Type</InputLabel>
            <Select value={periodType} onChange={e => setPeriodType(e.target.value as any)} label="Period Type">
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>

          {/* Time Range */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={selectedTimeRange}
              onChange={e => {
                setSelectedTimeRange(e.target.value);
                setShowCustomDatePicker(e.target.value === 'custom');
              }}
              label="Time Range"
            >
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
              <MenuItem value="ytd">Year to Date</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>

          {/* Custom date pickers */}
          {showCustomDatePicker && (
            <>
              <TextField
                size="small"
                label="Start Date"
                type="date"
                value={customDateRange.start}
                onChange={e => setCustomDateRange(p => ({ ...p, start: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="End Date"
                type="date"
                value={customDateRange.end}
                onChange={e => setCustomDateRange(p => ({ ...p, end: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          {/* KPI Filter */}
          {makeKpiAutocomplete()}

          <Button
            size="small"
            variant="text"
            startIcon={<Clear />}
            onClick={() => {
              setSelectedRegions([...REGIONS]);
              setPeriodType('weekly');
              setSelectedTimeRange('ytd');
              setShowCustomDatePicker(false);
              setCustomDateRange({ start: '', end: '' });
              setSelectedKPIs(['Count']);
            }}
          >
            Clear All
          </Button>

          {rawData.length > 0 && (
            <Chip
              label={`${filteredData.length} incidents`}
              size="small"
              sx={{ bgcolor: '#e3f2fd', color: '#0d47a1', fontWeight: 600 }}
            />
          )}
        </Box>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Paper elevation={2} sx={{ width: '100%', overflow: 'auto' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)', minHeight: 300 }}>
            <Table stickyHeader size="small">
              <TableHead>
                {/* Row 1: Timeline / period labels */}
                <TableRow>
                  <TableCell
                    sx={{
                      position: 'sticky', left: 0, zIndex: 3,
                      bgcolor: '#1565c0', color: '#fff',
                      fontWeight: 700, fontSize: '0.9rem', minWidth: 160, width: 160,
                      borderRight: 'none',
                    }}
                  >
                    Timeline
                  </TableCell>
                  <TableCell
                    sx={{
                      position: 'sticky', left: 160, zIndex: 3,
                      bgcolor: '#1565c0', minWidth: 80, width: 80,
                      borderRight: '2px solid #37474f',
                    }}
                  />
                  {periodsToDisplay.map(period => (
                    <Tooltip key={period} title={getPeriodDateRange(period, periodType)} placement="top" arrow>
                      <TableCell
                        colSpan={selectedKPIs.length || 1}
                        align="center"
                        sx={{
                          bgcolor: '#1565c0', color: '#fff',
                          fontWeight: 600, fontSize: '0.75rem',
                          borderRight: '2px solid #37474f',
                          cursor: 'help',
                        }}
                      >
                        {formatPeriodLabel(period, periodType)}
                      </TableCell>
                    </Tooltip>
                  ))}
                </TableRow>

                {/* Row 2: Metrics sub-header */}
                <TableRow>
                  <TableCell
                    sx={{
                      position: 'sticky', left: 0, zIndex: 3,
                      bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '0.85rem',
                      minWidth: 160, width: 160, borderRight: 'none',
                    }}
                  >
                    Metrics
                  </TableCell>
                  <TableCell
                    sx={{
                      position: 'sticky', left: 160, zIndex: 3,
                      bgcolor: '#e3f2fd', minWidth: 80, width: 80,
                      borderRight: '2px solid #37474f',
                    }}
                  />
                  {periodsToDisplay.map(period => (
                    <React.Fragment key={period}>
                      {selectedKPIs.includes('Count') && (
                        <TableCell align="center" sx={{ bgcolor: '#e3f2fd', fontWeight: 600, fontSize: '0.7rem', minWidth: 52 }}>Count</TableCell>
                      )}
                      {selectedKPIs.includes('MTTR') && (
                        <TableCell align="center" sx={{ bgcolor: '#e3f2fd', fontWeight: 600, fontSize: '0.7rem', minWidth: 68 }}>MTTR</TableCell>
                      )}
                      {selectedKPIs.includes('Lost Hrs') && (
                        <TableCell align="center" sx={{ bgcolor: '#e3f2fd', fontWeight: 600, fontSize: '0.7rem', minWidth: 58, borderRight: '2px solid #37474f' }}>Lost Hrs</TableCell>
                      )}
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {/* Per-region PLAN + ACTUAL row pairs */}
                {regionRows.filter(row => selectedRegions.length === 0 || selectedRegions.includes(row.region)).map(row => (
                  <React.Fragment key={row.region}>
                    {/* PLAN row */}
                    <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                      <TableCell
                        rowSpan={2}
                        sx={{
                          position: 'sticky', left: 0, zIndex: 2,
                          fontWeight: 700, bgcolor: 'white',
                          borderRight: 'none', fontSize: '0.9rem',
                          minWidth: 160, width: 160, verticalAlign: 'middle',
                        }}
                      >
                        {REGION_LABELS[row.region] || row.region}
                      </TableCell>
                      <TableCell
                        sx={{
                          position: 'sticky', left: 160, zIndex: 1,
                          bgcolor: '#e3f2fd', color: '#1565c0',
                          fontWeight: 600, fontSize: '0.75rem',
                          borderRight: '2px solid #37474f', minWidth: 80, width: 80,
                        }}
                      >
                        PLAN
                      </TableCell>
                      {periodsToDisplay.map(period => (
                        <React.Fragment key={period}>
                          {selectedKPIs.includes('Count') && (
                            <TableCell align="center" sx={{ fontWeight: 600, color: '#1565c0', fontSize: '0.8rem', bgcolor: '#e3f2fd', borderRight: lastKpi === 'Count' ? '2px solid #37474f' : '1px solid #e0e0e0' }}>
                              {target.incidentCount}
                            </TableCell>
                          )}
                          {selectedKPIs.includes('MTTR') && (
                            <TableCell align="center" sx={{ fontWeight: 600, color: '#1565c0', fontSize: '0.8rem', bgcolor: '#e3f2fd', borderRight: lastKpi === 'MTTR' ? '2px solid #37474f' : '1px solid #e0e0e0' }}>
                              {fmtMTTR(target.mttrAvgMinutes)}
                            </TableCell>
                          )}
                          {selectedKPIs.includes('Lost Hrs') && (
                            <TableCell align="center" sx={{ fontWeight: 600, color: '#1565c0', fontSize: '0.8rem', bgcolor: '#e3f2fd', borderRight: '2px solid #37474f' }}>
                              {target.lostHours}h
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                    </TableRow>

                    {/* ACTUAL row */}
                    <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                      <TableCell
                        sx={{
                          position: 'sticky', left: 160, zIndex: 1,
                          bgcolor: 'white', fontWeight: 600, fontSize: '0.75rem',
                          borderRight: '2px solid #37474f', minWidth: 80, width: 80,
                        }}
                      >
                        ACTUAL
                      </TableCell>
                      {periodsToDisplay.map(period =>
                        renderMetricCells(row.metrics[period], row.region, period)
                      )}
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5, px: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Legend:</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '2px' }} />
          <Typography variant="caption" color="text.secondary">Plan Targets</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#4caf50', borderRadius: '2px' }} />
          <Typography variant="caption" color="text.secondary">On Target</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#f44336', borderRadius: '2px' }} />
          <Typography variant="caption" color="text.secondary">Off Target</Typography>
        </Box>
      </Box>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            {detailData?.region} · {detailData?.period && formatPeriodLabel(detailData.period, periodType)} · {detailData?.incidents.length} incidents
          </span>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={() => {
                if (!detailData) return;
                const headers = ['Incident ID', 'Priority', 'Date', 'Region', 'Location', 'MTTR (min)', 'Description'];
                const rows = detailData.incidents.map(inc => [
                  inc.number,
                  `P${inc.priority}`,
                  inc.sys_created_on_value ? inc.sys_created_on_value.substring(0, 10) : '',
                  inc.region,
                  inc.location_display_value || '',
                  inc.ttr_seconds ? Math.round(inc.ttr_seconds / 60) : '',
                  (inc.short_description || '').replace(/"/g, '""'),
                ]);
                const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `incidents_${detailData.region}_${detailData.period}.csv`;
                a.click();
              }}
            >
              Export CSV
            </Button>
            <IconButton onClick={() => setDetailOpen(false)}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailData && (() => {
            const incs = detailData.incidents;
            const count = incs.length;
            const totalSec = incs.reduce((s, i) => s + (i.ttr_seconds || 0), 0);
            const mttrAvg = count > 0 ? totalSec / count / 60 : 0;
            const lostHrs = totalSec / 3600;
            const tgt = TARGETS[periodType];
            return (
              <>
                {/* Plan vs Actual summary */}
                <TableContainer component={Paper} elevation={1} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 700 }} />
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Incident Count</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Avg MTTR (hours)</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Lost Hours</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: '#fff3e0', color: '#e65100' }}>
                          📋 PLAN ({periodType.charAt(0).toUpperCase() + periodType.slice(1)} Targets)
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '1rem' }}>{tgt.incidentCount}</TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '1rem' }}>{fmtMTTR(tgt.mttrAvgMinutes)}</TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '1rem' }}>{tgt.lostHours.toFixed(2)}h</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Actual</TableCell>
                        <TableCell align="center" sx={{ backgroundColor: getCellColor(count, tgt.incidentCount), color: '#212121', fontWeight: 700, fontSize: '1rem' }}>{count}</TableCell>
                        <TableCell align="center" sx={{ backgroundColor: getCellColor(mttrAvg, tgt.mttrAvgMinutes), color: '#212121', fontWeight: 700, fontSize: '1rem' }}>{fmtMTTR(mttrAvg)}</TableCell>
                        <TableCell align="center" sx={{ backgroundColor: getCellColor(lostHrs, tgt.lostHours), color: '#212121', fontWeight: 700, fontSize: '1rem' }}>{lostHrs.toFixed(2)}h</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Incident list */}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Incident ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>P</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>MTTR</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {incs.slice(dialogPage * 10, (dialogPage + 1) * 10).map(inc => (
                        <TableRow key={inc.number}>
                          <TableCell sx={{ fontWeight: 600, color: '#1565c0' }}>{inc.number}</TableCell>
                          <TableCell>
                            <Chip label={`P${inc.priority}`} size="small"
                              sx={{ bgcolor: inc.priority === '1' ? '#f44336' : '#ff9800', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>
                            {inc.sys_created_on_value ? new Date(inc.sys_created_on_value).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{inc.location_display_value || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>
                            {inc.ttr_seconds ? fmtMTTR(inc.ttr_seconds / 60) : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.78rem', maxWidth: 280 }}>
                            <Tooltip title={inc.short_description || ''}>
                              <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {inc.short_description || '-'}
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={incs.length}
                  page={dialogPage}
                  onPageChange={(_, newPage) => setDialogPage(newPage)}
                  rowsPerPage={10}
                  rowsPerPageOptions={[10]}
                  sx={{ borderTop: '1px solid #e0e0e0' }}
                />
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default P1P2ByRegion;
